import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAllTenants, fetchTenantById, fetchUserTenantMemberships, type Tenant } from '@/core/api';
import { useFactoryStore, FACTORY_SCOPED_QUERY_PREFIXES } from '@/core/hooks/useFactory';
import { useAuth } from '@/core/AuthContext';

interface TenantState {
  selectedTenantId: string | null;
  isTransitioning: boolean;
  hasHydrated: boolean;
  setSelectedTenant: (id: string | null) => void;
  setTransitioning: (value: boolean) => void;
  setHydrated: (value: boolean) => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      selectedTenantId: null,
      isTransitioning: false,
      hasHydrated: false,
      setSelectedTenant: (id) => set({ selectedTenantId: id }),
      setTransitioning: (value) => set({ isTransitioning: value }),
      setHydrated: (value) => set({ hasHydrated: value }),
    }),
    {
      name: 'selected-tenant',
      partialize: (state) => ({ selectedTenantId: state.selectedTenantId }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    }
  )
);

const TENANT_SCOPED_QUERY_PREFIXES = [
  'user-factories',
  'tenant-members',
  'tenant-factories',
];

export const useTenantChange = () => {
  const queryClient = useQueryClient();
  const { setSelectedTenant, setTransitioning } = useTenantStore();

  const changeTenant = (newTenantId: string | null) => {
    const currentTenantId = useTenantStore.getState().selectedTenantId;

    if (currentTenantId === newTenantId) return;

    setTransitioning(true);

    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key)) return false;
        const prefix = key[0];
        if (typeof prefix !== 'string') return false;
        return [...TENANT_SCOPED_QUERY_PREFIXES, ...FACTORY_SCOPED_QUERY_PREFIXES].includes(prefix);
      },
    });

    useFactoryStore.getState().setSelectedFactory(null);
    setSelectedTenant(newTenantId);

    setTimeout(() => setTransitioning(false), 150);
  };

  return { changeTenant };
};

export const useUserTenants = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['tenant-memberships', user?.id],
    queryFn: fetchUserTenantMemberships,
    enabled: !!user,
  });
};

export const useAllTenants = (enabled: boolean) => {
  return useQuery({
    queryKey: ['all-tenants'],
    queryFn: fetchAllTenants,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTenant = () => {
  const { selectedTenantId, isTransitioning } = useTenantStore();
  const { changeTenant } = useTenantChange();

  const { data: selectedTenant, isLoading } = useQuery({
    queryKey: ['selected-tenant', selectedTenantId],
    queryFn: () => fetchTenantById(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    selectedTenantId,
    selectedTenant: (selectedTenant as Tenant | null) ?? null,
    setSelectedTenant: changeTenant,
    isLoading: isLoading || isTransitioning,
    isTransitioning,
  };
};
